import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation } from '@reach/router';
import Text from '../components/Text';
import Button from '../components/Button';
import Logo from '../components/Logo';
import { useDispatch } from 'react-redux';
import { authenticateMFA, cancelMfaRequest } from 'reducers/auth/actions';
import { navigate } from '@reach/router';
import { openErrorModal } from 'reducers/modal/actions';

const MFAPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  justify-content: center;
  position: relative;
  background: #e8eaeb;
`;

const MFAPanel = styled.form`
  height: 450px;
  width: 400px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
  display: flex;
  flex-direction: column;
  align-content: center;
  text-align: center;
`;

const MFAHeader = styled.div`
  display: inline-block;
  margin: 20px;
`;

const InputFields = styled.div`
  max-width: 265px;
  width: 100%;
  text-align: left;
  margin: 0px auto;
`;

const TextInput = styled.input`
  width: 100%;
  height: 30px;
  padding: 2px 8px;
  margin: 20px auto;
  font-size: 16px;
  border-radius: 2px;
  border: ${({ isError }) =>
    isError ? '1px solid #EA6E6D' : '1px solid #E5E7EB'};
  outline: none;

  ::placeholder {
    color: #4a4a4a;
    opacity: 0.5;
  }
`;

const UNEXPECTED_ERROR_MESSAGE =
  'An unexpected error has occurred, please contact your administrator';

export const MFA = ({ initialReturnUrl }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const returnUrl = location?.state?.returnUrl;
  const url = initialReturnUrl || returnUrl;
  const [mfa, setMFA] = useState('');
  const [mfaIsError, setMfaIsError] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const queryParams = new URLSearchParams(location.search);
  const emailParam = queryParams.get('email');
  const tokenParam = queryParams.get('mfaAuthToken');

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;
    const transformedValue = value.replace(/\D/g, '');

    if (transformedValue === '') {
      setMFA('');
      setSubmitDisabled(true);
      return;
    }
    setMFA(parseInt(transformedValue));
    setSubmitDisabled(false);
  };

  const handleCancelSubmission = async () => {
    try {
      await dispatch(cancelMfaRequest(emailParam, tokenParam));
      navigate('/');
    } catch (err) {
      // No need to handle the error modal here, as it's already handled in cancelMfaRequest
    }
  };

  const handleMFAError = err => {
    let errorMessage = '';
    if (err.status === 401 && err.message.remainingAttempts > 0) {
      errorMessage = `Invalid authorization code. Please try again. You have ${err.message.remainingAttempts} attempts remaining`;
      setMfaIsError(true);
    } else if (
      (err.message && err.message.remainingAttempts === 0) ||
      err.status === 400
    ) {
      setInputDisabled(true);
      dispatch(openErrorModal('Please try logging in again'));
      navigate('/');
    } else {
      errorMessage = UNEXPECTED_ERROR_MESSAGE;
      setMfaIsError(true);
    }

    setFormErrorMessage(errorMessage);
    setSubmitDisabled(true); // Set submitDisabled to true for all cases after handling the error.
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (mfa === '') {
      // Don't submit the form if there's no value for the MFA code
      return;
    }
    try {
      await dispatch(authenticateMFA(emailParam, tokenParam, mfa, url));
      navigate(returnUrl || '/');
    } catch (err) {
      handleMFAError(err);
    } finally {
      setMFA('');
    }
  };

  return (
    <MFAPage>
      <MFAPanel onSubmit={handleSubmit}>
        <Logo
          width="110px"
          height="50px"
          display="inline-block"
          margin="0px auto"
        />
        <MFAHeader>
          <Text fontSize="22px" padding="1px 1px 3px 3px">
            Two Factor Authentication
          </Text>
        </MFAHeader>
        <Text fontSize="12px" padding="1px 1px 3px 3px">
          Please authenticate your login by entering the
        </Text>
        <Text fontSize="12px" padding="1px 1px 3px 3px">
          Authorization code sent to: {emailParam}
        </Text>
        <Text fontSize="12px">If you do not see the verification email</Text>
        <Text fontSize="12px">make sure to check your spam folder.</Text>
        <InputFields>
          <TextInput
            name="mfaCode"
            type="tel"
            pattern="\d*"
            value={mfa}
            placeholder="Enter Code"
            onChange={handleInputChange}
            border={mfaIsError ? '1px solid #EA6E6D;' : null}
            autoFocus
            disabled={inputDisabled}
          />
        </InputFields>
        <Text color="#EA6E6D" fontSize="14px" margin="12px">
          {formErrorMessage}
        </Text>
        <Text fontSize="14px">
          <Text
            color="#0779af"
            fontSize="14px"
            margin="12px"
            cursor="pointer"
            onClick={handleCancelSubmission}
          >
            Cancel Login?
          </Text>
        </Text>

        <Button
          margin="0 auto 24px"
          color="#FFFFFF"
          padding="0px 30px"
          buttonHeight="30px"
          fontSize="14px"
          type="submit"
          disabled={submitDisabled}
        >
          Submit
        </Button>
      </MFAPanel>
    </MFAPage>
  );
};

export default MFA;
